import { ContentView } from "components";
import { useCallback, useEffect, useState } from "react";
import { ThreatDefensePhishingCampaignBody } from "./ThreatDefensePhishingCampaignBody";
import { ThreatDefenseRemedialTrainingBody } from "./ThreatDefenseRemedialTrainingBody";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { connect, type ConnectedProps } from "react-redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import styles from "./ThreatDefenseBody.module.scss";
import { setExportAction } from "features/Reporting/state/export/exportSlice";
import {
  getThreatDefenseCampaignExport,
  getThreatDefenseRemedialTrainingExport,
} from "../../actions/threatDefenseActions";
import { bindActionCreators } from "redux";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";

type Props = PropsFromRedux & {
  campaignId: number;
  campaignName: string;
  filter: PerformanceWithAccountFilter;
  registerBreadcrumb: (breadcrumb: { text: string; action: () => void }) => void;
  goBackNLevels: (n: number) => void;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

export const ThreatDefenseBody = ({
  campaignId,
  filter,
  registerBreadcrumb,
  goBackNLevels,
  campaignName,
  setIsReportEnabled,
  resetIsReportEnabled,
  setExport,
  threatDefenseCampaignExport,
  getThreatDefenseRemedialTrainingExport,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const isReportEnabled = useFeatureFlag(FeatureFlags.ThreatDefenseReport);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedSessionId, setSelectedSessionId] = useState<number | undefined>(undefined);

  // Setting appropriate export method
  useEffect(() => {
    setExport({
      method: activeTab === 0 ? threatDefenseCampaignExport : getThreatDefenseRemedialTrainingExport,
      args: [{ ...filter, campaignId, campaignRunId: selectedSessionId }, campaignName],
      isExportEnabled: true,
    });
  }, [
    filter,
    getThreatDefenseRemedialTrainingExport,
    threatDefenseCampaignExport,
    activeTab,
    setExport,
    campaignId,
    selectedSessionId,
    campaignName,
  ]);

  const handleSendSelection = useCallback(
    (session: string, campaignRunId: number) => {
      registerBreadcrumb({
        text: session,
        action: () => {
          setSelectedSessionId(undefined);
        },
      });
      setSelectedSessionId(campaignRunId);
    },
    [registerBreadcrumb],
  );

  useEffect(() => {
    setIsReportEnabled(isReportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [isReportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const contentViewTabs = [
    {
      id: "phishingCampaign",
      label: "Phishing Campaign",
      component: (
        <ThreatDefensePhishingCampaignBody
          handleSendSelection={handleSendSelection}
          campaignId={campaignId}
          selectedSessionId={selectedSessionId}
          filter={filter}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      ),
    },
    {
      id: "remedialTraining",
      label: "Remedial Training",
      component: (
        <ThreatDefenseRemedialTrainingBody
          campaignId={campaignId}
          filter={filter}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      ),
    },
  ];

  if (!isReportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.body}>
      <ContentView
        tabs={contentViewTabs}
        activeIndex={activeTab}
        onTabChange={(_, r: { activeIndex: number }) => {
          setActiveTab(r.activeIndex);

          if (selectedSessionId) {
            goBackNLevels(1);
          }
          setSelectedSessionId?.(undefined);
        }}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  setExport: bindAction(setExportAction, dispatch),

  getThreatDefenseRemedialTrainingExport: bindActionCreators(getThreatDefenseRemedialTrainingExport, dispatch),
  threatDefenseCampaignExport: bindActionCreators(getThreatDefenseCampaignExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ThreatDefenseBody);
export default ConnectedComponent;
